import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CheckCircle2, ChevronLeft, Pencil, Play, Send, XCircle } from 'lucide-react';
import { ACTION_PRIORITIES, type ActionPriority } from '@bp/shared';
import { api } from '@/lib/api';
import type { ActionItemDetail, MediaRow } from '@/lib/types';
import { fmtDate, fmtDateTime } from '@/lib/utils';
import { ActionStatusBadge, ModeBadge, PriorityBadge } from '@/components/badges';
import { Button, Card, CardHeader, Dialog, ErrorNote, Field, Input, Loading, Select, Textarea } from '@/components/ui';
import { CaptureButtons, MediaThumbs } from '@/components/audit/Media';
import { AssigneeFields } from '@/components/audit/CreateTaskDialog';

const eventText: Record<string, string> = {
  created: 'created the task', started: 'started work', submitted: 'submitted proof', verified: 'verified the fix',
  rejected: 'sent it back', comment: 'commented', updated: 'updated the task', overdue: 'Flagged overdue',
};

export function TaskDetailPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['task', id], queryFn: () => api.get<ActionItemDetail>(`/api/action-items/${id}`) });
  const [proof, setProof] = useState<MediaRow[]>([]);
  const [uploading, setUploading] = useState(0);
  const [note, setNote] = useState('');
  const [comment, setComment] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorNote error={q.error} />;
  const t = q.data!;
  const refresh = async () => {
    await q.refetch();
    await qc.invalidateQueries({ queryKey: ['tasks'] });
  };
  const act = async (name: string, fn: () => Promise<unknown>, ok: string) => {
    setBusy(name);
    try {
      await fn();
      toast.success(ok);
      await refresh();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(null);
    }
  };
  const upload = async (files: File[]) => {
    for (const f of files) {
      setUploading((n) => n + 1);
      try {
        const m = await api.upload<MediaRow>({ action_item_id: t.id }, f, f.name || 'proof.jpg');
        setProof((p) => [...p, m]);
      } catch (e) {
        toast.error((e as Error).message);
      } finally {
        setUploading((n) => n - 1);
      }
    }
  };
  // Proof photos not yet attached to a submission (from an earlier, unfinished attempt) are reused.
  const unsent = [...t.media.filter((m) => !m.action_event_id), ...proof.filter((p) => !t.media.some((m) => m.id === p.id))];

  return (
    <>
      <Link to="/tasks" className="mb-2 inline-flex items-center gap-1 text-sm text-muted-fg hover:text-fg"><ChevronLeft className="size-4" />Action items</Link>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold sm:text-2xl">{t.title}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <ActionStatusBadge status={t.status} overdueDays={t.overdue_days} />
            <PriorityBadge priority={t.priority} />
          </div>
        </div>
        {t.can.edit && <Button variant="outline" onClick={() => setEditing(true)}><Pencil className="size-4" />Edit</Button>}
      </div>

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          {t.can.work && (
            <Card className="border-info/40">
              <CardHeader title="Fix it and show proof" subtitle="Take a photo of the fixed issue, add a note, and submit for verification." />
              <div className="space-y-3 p-4">
                {(t.status === 'open' || t.status === 'reopened') && (
                  <Button variant="outline" loading={busy === 'start'} onClick={() => act('start', () => api.post(`/api/action-items/${t.id}/start`), 'Marked in progress.')}><Play className="size-4" />I'm working on it</Button>
                )}
                <CaptureButtons onFiles={upload} label="Add proof photo" disabled={uploading > 0} />
                {uploading > 0 && <p className="text-sm text-muted-fg">Uploading…</p>}
                <MediaThumbs items={unsent} />
                <Textarea placeholder="What was done?" value={note} onChange={(e) => setNote(e.target.value)} />
                <Button
                  size="lg"
                  disabled={unsent.length === 0}
                  loading={busy === 'submit'}
                  onClick={() => act('submit', async () => {
                    await api.post(`/api/action-items/${t.id}/submit`, { note, media_ids: unsent.map((m) => m.id) });
                    setProof([]);
                    setNote('');
                  }, 'Submitted for verification.')}
                ><Send className="size-4" />Submit for verification</Button>
                {unsent.length === 0 && <p className="text-xs text-muted-fg">At least one photo is required.</p>}
              </div>
            </Card>
          )}

          {t.can.verify && (
            <Card className="border-practice/40">
              <CardHeader title="Check the proof" subtitle="Look at the photos below, then verify or send it back." />
              <div className="flex flex-wrap gap-2 p-4">
                <Button size="lg" loading={busy === 'verify'} onClick={() => act('verify', () => api.post(`/api/action-items/${t.id}/verify`, { note: '' }), 'Verified and closed.')}><CheckCircle2 className="size-5" />Verify & close</Button>
                <Button size="lg" variant="outline" onClick={() => setRejecting(true)}><XCircle className="size-5" />Send back</Button>
              </div>
            </Card>
          )}

          <Card>
            <CardHeader title="History" />
            <ol className="divide-y">
              {t.events.map((e) => {
                const photos = t.media.filter((m) => m.action_event_id === e.id);
                return (
                  <li key={e.id} className="px-4 py-3 text-sm">
                    <p><strong>{e.type === 'overdue' ? '' : e.actor_name}</strong> {eventText[e.type] ?? e.type} <span className="text-muted-fg">· {fmtDateTime(e.created_at)}</span></p>
                    {e.note && <p className="mt-1 whitespace-pre-wrap">{e.note}</p>}
                    {photos.length > 0 && <div className="mt-2"><MediaThumbs items={photos} /></div>}
                  </li>
                );
              })}
            </ol>
            <div className="flex gap-2 border-t p-3">
              <Input placeholder="Add a comment" value={comment} onChange={(e) => setComment(e.target.value)} />
              <Button variant="outline" disabled={!comment.trim()} loading={busy === 'comment'} onClick={() => act('comment', async () => { await api.post(`/api/action-items/${t.id}/comment`, { note: comment }); setComment(''); }, 'Comment added.')}>Post</Button>
            </div>
          </Card>
        </div>

        <div className="space-y-5">
          <Card>
            <CardHeader title="Details" />
            <dl className="divide-y text-sm">
              {[
                ['Store', t.store.name],
                ['Assigned to', t.assignee_name || 'Unassigned'],
                ['Due', t.due_date ? fmtDate(t.due_date) : 'No due date'],
                ['Created', fmtDateTime(t.created_at)],
                ...(t.verified_at ? [['Verified', fmtDateTime(t.verified_at)]] : []),
              ].map(([k, v]) => <div key={k} className="flex justify-between gap-3 px-4 py-2"><dt className="text-muted-fg">{k}</dt><dd className="text-right font-medium">{v}</dd></div>)}
            </dl>
            {t.detail && <p className="border-t px-4 py-3 text-sm whitespace-pre-wrap">{t.detail}</p>}
          </Card>
          {t.audit && (
            <Card>
              <CardHeader title="From audit" />
              <div className="space-y-2 px-4 py-3 text-sm">
                <p className="flex items-center gap-2"><Link to={`/audits/${t.audit.id}`} className="font-medium text-info hover:underline">{fmtDate(t.audit.audit_date)}</Link><ModeBadge mode={t.audit.mode} /></p>
                <p className="text-muted-fg">Auditor: {t.audit.auditor_name}</p>
                {t.audit_media.length > 0 && <MediaThumbs items={t.audit_media as MediaRow[]} size="sm" />}
              </div>
            </Card>
          )}
        </div>
      </div>

      <Dialog
        open={rejecting}
        onOpenChange={setRejecting}
        title="Send back for more work?"
        footer={<><Button variant="outline" onClick={() => setRejecting(false)}>Cancel</Button><Button variant="danger" loading={busy === 'reject'} onClick={() => act('reject', async () => { await api.post(`/api/action-items/${t.id}/reject`, { reason: note }); setRejecting(false); setNote(''); }, 'Sent back.')}>Send back</Button></>}
      >
        <Field label="What's still wrong?"><Textarea value={note} onChange={(e) => setNote(e.target.value)} /></Field>
      </Dialog>

      {editing && <EditTaskDialog task={t} onClose={() => setEditing(false)} onSaved={refresh} />}
    </>
  );
}

function EditTaskDialog({ task, onClose, onSaved }: { task: ActionItemDetail; onClose: () => void; onSaved: () => Promise<void> }) {
  const [title, setTitle] = useState(task.title);
  const [detail, setDetail] = useState(task.detail);
  const [priority, setPriority] = useState<ActionPriority>(task.priority);
  const [due, setDue] = useState(task.due_date ?? '');
  const [assignee, setAssignee] = useState({ assignee_user_id: task.assignee_user_id, assignee_worker_id: task.assignee_worker_id });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  async function save() {
    setBusy(true);
    try {
      const body: Record<string, unknown> = {};
      if (title !== task.title) body.title = title;
      if (detail !== task.detail) body.detail = detail;
      if (priority !== task.priority) body.priority = priority;
      if ((due || null) !== task.due_date) body.due_date = due || null;
      if (assignee.assignee_user_id !== task.assignee_user_id || assignee.assignee_worker_id !== task.assignee_worker_id) Object.assign(body, assignee);
      if (Object.keys(body).length) await api.patch(`/api/action-items/${task.id}`, body);
      await onSaved();
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title="Edit action item" footer={<><Button variant="outline" onClick={onClose}>Cancel</Button><Button loading={busy} onClick={save}>Save</Button></>}>
      <div className="space-y-4">
        <Field label="Title"><Input value={title} onChange={(e) => setTitle(e.target.value)} /></Field>
        <Field label="Details"><Textarea value={detail} onChange={(e) => setDetail(e.target.value)} /></Field>
        <AssigneeFields storeId={task.store_id} userId={assignee.assignee_user_id} workerId={assignee.assignee_worker_id} onChange={setAssignee} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Due date"><Input type="date" value={due} onChange={(e) => setDue(e.target.value)} /></Field>
          <Field label="Priority"><Select value={priority} onChange={(e) => setPriority(e.target.value as ActionPriority)}>{ACTION_PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}</Select></Field>
        </div>
        <ErrorNote error={error} />
      </div>
    </Dialog>
  );
}
